import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Services from 'services'
import useSWR from 'swr'
import { SWR_COMPANY } from 'services/swr.keys'
import { TUser } from 'models/user.model'
import { IRegister } from 'models/register.model'
import { toast } from 'react-toastify'
import { toastStyle } from 'util/toast'
import generateErrorMessage from 'util/errorMessage'
import Box from 'ui/src/components/Box'
import Typography from 'ui/src/components/Typography'
import Concierge from './components/Concierge'
import Referrals from './components/Referrals'
import Notifications from './components/Notifications'
import LanguageSelectorContainer from './components/LanguageSelectorContainer'
import ProfileMenu from './components/ProfileMenu'
import LogoCircle from 'ui/src/assets/images/ProtexxaLogos/ProtexxaLogo-circle.svg?react'
import { useTheme, useMediaQuery } from '@mui/material'

interface AppHeaderV2Props {
  title?: string
  user?: TUser | undefined
  register?: (IRegister & { status: 'completed' | 'pending' }) | undefined
  logout?: () => void
  avatarChanged?: boolean
  avatar?: string
  setAvatar?: (avatar: string) => void
  getAvatar?: () => Promise<void>
  referrals?: boolean
  hasHighLevelPrivileges?: boolean
  showLogo?: boolean
  hideAvatar?: boolean
  isLogoDisabled?: boolean
  hideNotifications?: boolean
}

const AppHeaderV2 = ({
  title,
  user,
  register,
  logout,
  avatarChanged,
  getAvatar,
  avatar,
  setAvatar,
  referrals,
  hasHighLevelPrivileges = false,
  showLogo = false,
  hideAvatar = false,
  isLogoDisabled = false,
  hideNotifications = false,
}: AppHeaderV2Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const theme = useTheme()

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const isCompanyPage = useMemo(
    () => location.pathname.includes('/company') && !location.pathname.includes('individual'),
    [location.pathname],
  )

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [allNotifications, setAllNotifications] = useState<any>([])
  const [refetchData, setRefetchData] = useState(false)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const [profileSelected, setProfileSelected] = useState<'individual' | 'company'>('company')

  const handleLanguageChange = useCallback(
    async (language: string) => {
      const langCode = language.split('-')[0]
      if (user) await Services.User.updateLanguage(langCode)
    },
    [user],
  )

  const { data: companyData } = useSWR(
    hasHighLevelPrivileges ? SWR_COMPANY : null,
    () => user && Services.Company.getCompanyV2(),
  )

  const { data: avatarBlob } = useSWR('SWR_AVATAR', () => user && Services.Auth.getImageBlob())

  const userAvatar = avatarBlob || avatar
  const companyAvatar = companyData?.image || avatar

  const notificationCategoryMapping = useMemo(
    () => ({
      CRITICAL: 'critical',
      IMPORTANT: 'important',
      REPORT: 'image',
      REMINDER: 'reminders',
      COMPANY: 'company',
    }),
    [],
  )

  useEffect(() => {
    if (!user) return
    const fetchNotifications = async () => {
      const result = await Services.Notifications.getAllNotifications()
      const userNotifications = result.filter(
        notification => notification.notificationCategory !== 'COMPANY',
      )
      const notifications = hasHighLevelPrivileges ? result : userNotifications
      const filteredNotifications = notifications.filter(notification => {
        const category = notificationCategoryMapping[notification.notificationCategory]
        return register?.notificationSettings?.push[category]
      })
      setAllNotifications(filteredNotifications)
    }
    fetchNotifications()
  }, [refetchData, register, notificationCategoryMapping, user, hasHighLevelPrivileges])

  useEffect(() => {
    if (allNotifications?.some(notification => !notification.isRead)) {
      setHasUnreadNotifications(true)
    } else {
      setHasUnreadNotifications(false)
    }
  }, [allNotifications])

  const handleSignOut = useCallback(async () => {
    try {
      logout?.()
      navigate('/')
    } catch (error) {
      if (error instanceof Error) toast.error(generateErrorMessage(error.message), toastStyle)
    }
  }, [logout, navigate])

  useEffect(() => {
    if (isCompanyPage) {
      setProfileSelected('company')
    } else {
      setProfileSelected('individual')
    }
  }, [isCompanyPage])

  useEffect(() => {
    if (isCompanyPage && companyData && setAvatar) setAvatar(companyData.image)
    if (!isCompanyPage && register && getAvatar) getAvatar()
  }, [
    companyData,
    register,
    hasHighLevelPrivileges,
    isCompanyPage,
    avatarChanged,
    getAvatar,
    setAvatar,
  ])

  const handleModalClose = () => {
    setIsNotificationsOpen(false)
    setIsProfileMenuOpen(false)
  }

  const Backdrop = useMemo(() => {
    if (!isNotificationsOpen && !isProfileMenuOpen) return null

    return (
      <Box
        onClick={handleModalClose}
        role="presentation"
        aria-label="Close modal"
        sx={{
          width: '100vw',
          height: '100vh',
          position: 'absolute',
          left: 0,
          top: 0,
          zIndex: 5,
        }}
      />
    )
  }, [isNotificationsOpen, isProfileMenuOpen])

  const handleLogoClick = () => {
    if (isLogoDisabled) return

    if (hasHighLevelPrivileges) navigate('/company/dashboard')
    else navigate('/dashboard')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'gray.0',
        padding: '1.25rem 2rem',
        height: '5rem',
        width: '100%',
        borderBottom: '0.063rem solid',
        borderColor: 'gray.100',
      }}
    >
      {!isSmallScreen && (
        <Box
          sx={{
            paddingRight: '2rem',
            width: 'fit-content',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'gray.800',
              whiteSpace: 'nowrap',
            }}
          >
            {title && t(`default.appSidebar.${title}`)}
          </Typography>
          {showLogo && (
            <Box
              sx={{
                pl: 2,
              }}
            >
              <LogoCircle
                style={{ cursor: !isLogoDisabled ? 'pointer' : 'default' }}
                onClick={handleLogoClick}
              />
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%',
          gap: isSmallScreen ? '1rem' : '1.5rem',
        }}
      >
        {register && <Concierge />}
        {referrals && <Referrals />}
        {register && !hideNotifications && (
          <Notifications
            isNotificationsOpen={isNotificationsOpen}
            setIsNotificationsOpen={setIsNotificationsOpen}
            allNotifications={allNotifications}
            setRefetchData={setRefetchData}
            hasUnreadNotifications={hasUnreadNotifications}
          />
        )}
        {user && !hideAvatar && (
          <ProfileMenu
            avatar={avatar ? avatar : ''}
            handleSignOut={handleSignOut}
            isProfileMenuOpen={isProfileMenuOpen}
            user={user}
            hasHighLevelPrivileges={hasHighLevelPrivileges}
            setIsProfileMenuOpen={setIsProfileMenuOpen}
            userAvatar={userAvatar ? userAvatar : ''}
            companyAvatar={companyAvatar ? companyAvatar : ''}
            profileSelected={profileSelected}
            register={register}
          />
        )}
        <LanguageSelectorContainer handleLanguageChange={handleLanguageChange} />
      </Box>
      {Backdrop}
    </Box>
  )
}

export default AppHeaderV2
